import type { StrategistTransition } from '@/entities/strategist';
import { conceptLabel } from './lib';

/** Колонка диаграммы: слева возражения клиента, справа аргументы оператора. */
export type SankeySide = 'objection' | 'argument';

/** Узел диаграммы переходов — понятие в своей колонке. */
export type SankeyNodeItem = {
  /** Подпись узла; её же читает библиотека как имя узла */
  name: string;
  /** Понятие узла — им открывается карточка понятия */
  conceptId: string;
  side: SankeySide;
};

/** Лента диаграммы — переход между узлами колонок, толщина ленты равна числу диалогов. */
export type SankeyLinkItem = {
  /** Индекс узла-возражения в наборе узлов */
  source: number;
  /** Индекс узла-аргумента в наборе узлов */
  target: number;
  /** Число диалогов перехода — им библиотека считает толщину ленты */
  value: number;
  /** Исходный переход — им открываются диалоги среза */
  transition: StrategistTransition;
};

/** Набор диаграммы: узлы обеих колонок и ленты между ними. */
export type SankeyGraph = {
  nodes: SankeyNodeItem[];
  links: SankeyLinkItem[];
};

/**
 * Собирает набор диаграммы из переходов. Одно и то же понятие в разных колонках — разные узлы:
 * слева оно прозвучало возражением, справа аргументом, и объединять их в один узел значило бы
 * склеить два разных места разговора.
 */
export const buildSankeyGraph = (transitions: StrategistTransition[]): SankeyGraph => {
  const nodes: SankeyNodeItem[] = [];
  const indexBySideAndConcept = new Map<string, number>();

  const nodeIndex = (conceptId: string, label: string | null, side: SankeySide): number => {
    const key = `${side}__${conceptId}`;
    const known = indexBySideAndConcept.get(key);
    if (known !== undefined) return known;
    const index = nodes.length;
    nodes.push({ name: conceptLabel(label, conceptId), conceptId, side });
    indexBySideAndConcept.set(key, index);
    return index;
  };

  const links = transitions.map((transition) => ({
    source: nodeIndex(transition.fromConceptId, transition.fromConcept, 'objection'),
    target: nodeIndex(transition.toConceptId, transition.toConcept, 'argument'),
    value: transition.n,
    transition,
  }));

  return { nodes, links };
};

/** Число узлов в самой длинной колонке — по нему диаграмме отводится высота. */
export const maxColumnSize = (nodes: SankeyNodeItem[]): number => {
  const objections = nodes.filter((node) => node.side === 'objection').length;
  return Math.max(objections, nodes.length - objections);
};
